import { FC } from 'react';
import { FallbackProps } from 'react-error-boundary';
import { Box, Button, Typography, Paper } from '@mui/material';
import { ErrorOutline } from '@mui/icons-material';

const ErrorFallback: FC<FallbackProps> = ({ error, resetErrorBoundary }) => {
    const isDevelopment = import.meta.env.VITE_APP_ENV === 'development' || import.meta.env.DEV;

    // Always log error to console (even in production for debugging)
    if (error) {
        console.error('🚨 React Error Boundary Caught an Error:', error);
        if (error instanceof Error) {
            console.error('Error Stack:', error.stack);
        }
    }

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 3,
                backgroundColor: 'var(--color-background)',
            }}
        >
            <Paper
                elevation={3}
                sx={{
                    maxWidth: 800,
                    width: '100%',
                    padding: 4,
                    borderRadius: 2,
                }}
            >
                <Box className="flex items-center gap-2" sx={{ mb: 3 }}>
                    <ErrorOutline sx={{ fontSize: 48, color: 'error.main' }} />
                    <Typography variant="h4" component="h1" color="error">
                        Something went wrong
                    </Typography>
                </Box>

                <Typography variant="body1" paragraph sx={{ mb: 2 }}>
                    {isDevelopment
                        ? 'An unexpected error occurred. Please check the browser console for more details.'
                        : 'We apologize, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.'}
                </Typography>

                {/* Only show error details in development */}
                {isDevelopment && error && (
                    <Box
                        sx={{
                            backgroundColor: 'var(--color-surface-light)',
                            padding: 2,
                            borderRadius: 1,
                            mb: 3,
                            border: '1px solid var(--color-border)',
                        }}
                    >
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Error Message:
                        </Typography>
                        <Typography
                            variant="body2"
                            component="pre"
                            sx={{
                                fontFamily: 'monospace',
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                                color: 'var(--color-error)',
                            }}
                        >
                            {error.toString()}
                        </Typography>
                    </Box>
                )}

                <Box className="flex gap-2 justify-end">
                    <Button variant="outlined" onClick={resetErrorBoundary}>
                        Try Again
                    </Button>
                    <Button variant="contained" onClick={() => window.location.reload()} sx={{ backgroundColor: 'var(--color-primary)' }}>
                        Reload Page
                    </Button>
                </Box>

                {/* Only show console tip in development */}
                {isDevelopment && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block' }}>
                        💡 Tip: Open browser DevTools (F12) and check the Console tab for detailed error information.
                    </Typography>
                )}
            </Paper>
        </Box>
    );
};

export default ErrorFallback;
